import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CheckoutContext } from '@vivport/backend/types';
import {
  getBillingSummary,
  getPlanOffer,
  getEntitlement,
  confirmSubscription,
  startSubscriptionCheckout,
  startTopup,
  openBillingPortal,
  getPaymentMethod,
  getInvoices,
  getUsage,
  getChatSpend,
  setAutoTopup,
} from '@/api/billing-api';

export const billingKeys = {
  all: ['billing'] as const,
  summary: () => [...billingKeys.all, 'summary'] as const,
  planOffer: () => [...billingKeys.all, 'planOffer'] as const,
  entitlement: () => [...billingKeys.all, 'entitlement'] as const,
  paymentMethod: () => [...billingKeys.all, 'paymentMethod'] as const,
  invoices: () => [...billingKeys.all, 'invoices'] as const,
  usage: (params?: { from?: string; to?: string }) => [...billingKeys.all, 'usage', params ?? {}] as const,
  spends: () => [...billingKeys.all, 'spend'] as const,
  spend: (contentId: number) => [...billingKeys.all, 'spend', contentId] as const,
};

export function useBillingSummary() {
  return useQuery({
    queryKey: billingKeys.summary(),
    queryFn: getBillingSummary,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function usePlanOffer() {
  return useQuery({
    queryKey: billingKeys.planOffer(),
    queryFn: getPlanOffer,
    staleTime: 60_000,
  });
}

export function useEntitlement() {
  return useQuery({
    queryKey: billingKeys.entitlement(),
    queryFn: getEntitlement,
    staleTime: 15_000,
  });
}

export function useConfirmSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: confirmSubscription,
    onSuccess: (entitlement) => {
      queryClient.setQueryData(billingKeys.entitlement(), entitlement);
      queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
    },
  });
}

export function useStartSubscription(context?: CheckoutContext) {
  return useMutation({
    mutationFn: () => startSubscriptionCheckout(context),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });
}

export function useStartTopup() {
  return useMutation({
    mutationFn: (amountAud: number) => startTopup(amountAud),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });
}

export function useOpenPortal() {
  return useMutation({
    mutationFn: openBillingPortal,
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });
}

export function usePaymentMethod() {
  return useQuery({
    queryKey: billingKeys.paymentMethod(),
    queryFn: getPaymentMethod,
    staleTime: 60_000,
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: billingKeys.invoices(),
    queryFn: getInvoices,
    staleTime: 60_000,
  });
}

export function useUsage(params?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: billingKeys.usage(params),
    queryFn: () => getUsage(params),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useSetAutoTopup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setAutoTopup,
    onSuccess: (res) => {
      if (res.setupRequired) {
        window.location.href = res.setupUrl;
      } else {
        queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
      }
    },
  });
}

export function useChatSpend(contentId?: number) {
  const id = contentId ?? 0;
  return useQuery({
    queryKey: billingKeys.spend(id),
    queryFn: () => getChatSpend(id),
    enabled: id > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
